import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  Clock,
  Database,
  Download,
  Play,
  Eye,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  UserX,
  Trash2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import type { DataRetentionDashboardResponse } from '../types';

const queryKeys = {
  retentionDashboard: ['dataRetention', 'dashboard'] as const,
};

export default function DataRetentionDashboard() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [entityTypeFilter, setEntityTypeFilter] = useState('');
  const [confirmPurge, setConfirmPurge] = useState(false);

  // GDPR erasure audit log
  const { data: gdprAudits } = useQuery({
    queryKey: ['gdpr', 'audits', 1],
    queryFn: () => api.getDeletionAudits(1, 5),
  });

  const { data, isLoading, error } = useQuery<DataRetentionDashboardResponse>({
    queryKey: [...queryKeys.retentionDashboard, { page, entityType: entityTypeFilter }],
    queryFn: () =>
      api.getDataRetentionDashboard({
        page,
        limit: 20,
        entityType: entityTypeFilter || undefined,
      }),
    refetchInterval: 60000,
  });

  const runPurge = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const res = await fetch('/api/data-retention/run-purge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dryRun }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Request failed' }));
        throw new Error(err.error || err.message || `HTTP ${res.status}`);
      }
      return res.json() as Promise<{ success: boolean; message: string; dryRun: boolean }>;
    },
    onSuccess: (result) => {
      showToast(result.message, 'success');
      queryClient.invalidateQueries({ queryKey: queryKeys.retentionDashboard });
      setConfirmPurge(false);
    },
    onError: (err: Error) => {
      showToast(err.message, 'error');
    },
  });

  const handleExportCsv = async () => {
    try {
      const blob = await api.exportDataRetentionAuditLog({
        entityType: entityTypeFilter || undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `data-retention-audit-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      showToast('Audit log exported successfully', 'success');
    } catch (err) {
      showToast('Failed to export audit log', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin h-8 w-8 border-2 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-4">
        <p className="text-red-800 dark:text-red-300">Failed to load data retention dashboard: {(error as Error).message}</p>
      </div>
    );
  }

  if (!data) return null;

  const { policy, nextPurge, dataCounts, auditLog, pagination } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Shield className="h-6 w-6 text-blue-500" />
            Data Retention
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Monitor data retention policies, expiring records, and purge history
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => runPurge.mutate(true)}
            disabled={!policy.enabled || runPurge.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={!policy.enabled ? 'Enable data retention in Settings first' : 'Preview what would be purged'}
          >
            <Eye className="h-4 w-4" />
            Dry Run
          </button>
          <button
            onClick={() => setConfirmPurge(true)}
            disabled={!policy.enabled || runPurge.isPending}
            className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={!policy.enabled ? 'Enable data retention in Settings first' : 'Run purge now'}
          >
            <Play className="h-4 w-4" />
            Run Now
          </button>
        </div>
      </div>

      {/* Confirm Purge Dialog */}
      {confirmPurge && (
        <div className="rounded-lg bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium text-red-800 dark:text-red-300">Confirm Data Purge</p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                This will permanently delete data that exceeds retention periods. This action cannot be undone.
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => runPurge.mutate(false)}
                  disabled={runPurge.isPending}
                  className="inline-flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {runPurge.isPending ? 'Running...' : 'Yes, Run Purge'}
                </button>
                <button
                  onClick={() => setConfirmPurge(false)}
                  className="inline-flex items-center gap-1 rounded-md bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Policy Status + Next Purge */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Retention Policy
          </h3>
          <div className="flex items-center gap-2 mb-3">
            {policy.enabled ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 dark:bg-green-900 px-2.5 py-0.5 text-xs font-medium text-green-800 dark:text-green-300">
                <CheckCircle className="h-3 w-3" /> Enabled
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400">
                <XCircle className="h-3 w-3" /> Disabled
              </span>
            )}
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Jobs</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{policy.jobRetentionDays} days</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Applications</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{policy.applicationRetentionDays} days</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Candidates</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{policy.candidateRetentionDays} days</span>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Next Scheduled Purge
          </h3>
          <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
            {nextPurge.scheduledAt
              ? new Date(nextPurge.scheduledAt).toLocaleString()
              : 'Not scheduled'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Cron: {nextPurge.cronExpression}
          </p>
        </div>
      </div>

      {/* Data Counts */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
          <Database className="h-4 w-4" />
          Data Overview
        </h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {/* Jobs */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Jobs</h4>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{dataCounts.jobs.total}</p>
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Inactive</span>
                <span className="font-medium text-gray-700 dark:text-gray-300">{dataCounts.jobs.inactive}</span>
              </div>
              {dataCounts.jobs.expiringSoon > 0 && (
                <div className="flex justify-between">
                  <span className="text-orange-500">Expiring Soon</span>
                  <span className="font-medium text-orange-600 dark:text-orange-400">{dataCounts.jobs.expiringSoon}</span>
                </div>
              )}
            </div>
          </div>
          {/* Applications */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Applications</h4>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{dataCounts.applications.total}</p>
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Inactive</span>
                <span className="font-medium text-gray-700 dark:text-gray-300">{dataCounts.applications.inactive}</span>
              </div>
              {dataCounts.applications.expiringSoon > 0 && (
                <div className="flex justify-between">
                  <span className="text-orange-500">Expiring Soon</span>
                  <span className="font-medium text-orange-600 dark:text-orange-400">{dataCounts.applications.expiringSoon}</span>
                </div>
              )}
            </div>
          </div>
          {/* Candidates */}
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Candidates</h4>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{dataCounts.candidates.total}</p>
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Orphaned</span>
                <span className="font-medium text-gray-700 dark:text-gray-300">{dataCounts.candidates.orphaned}</span>
              </div>
              {dataCounts.candidates.expiringSoon > 0 && (
                <div className="flex justify-between">
                  <span className="text-orange-500">Expiring Soon</span>
                  <span className="font-medium text-orange-600 dark:text-orange-400">{dataCounts.candidates.expiringSoon}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* GDPR Erasure Requests + Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* GDPR Requests */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <UserX className="h-4 w-4" />
              Recent GDPR Erasures
            </h3>
            <Link
              to="/gdpr-erasure"
              className="text-xs text-purple-600 hover:text-purple-700 font-medium"
            >
              View All
            </Link>
          </div>
          {gdprAudits?.data && gdprAudits.data.length > 0 ? (
            <div className="space-y-2">
              {gdprAudits.data.map((audit) => (
                <div key={audit.id} className="flex items-center justify-between text-xs p-2 bg-gray-50 dark:bg-gray-800 rounded">
                  <div>
                    <span className="font-mono text-gray-500 dark:text-gray-400">
                      {audit.candidateEmailHash.slice(0, 8)}...
                    </span>
                    <span className="ml-2 text-gray-400 dark:text-gray-500">
                      {new Date(audit.requestedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{audit.totalRecordsDeleted} records</span>
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
                      audit.status === 'completed'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                    }`}>
                      {audit.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 dark:text-gray-500">No GDPR erasure requests processed yet.</p>
          )}
        </div>

        {/* Deletion Statistics */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <Trash2 className="h-4 w-4" />
            Deletion Summary
          </h3>
          <div className="space-y-3">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">GDPR Erasures (Total)</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {gdprAudits?.pagination?.total ?? 0}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Automated Purge Events</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {auditLog.length > 0 ? pagination.total : 0}
              </p>
            </div>
            <div className="pt-2 border-t border-gray-100 dark:border-gray-800">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Expiring soon: {(dataCounts.jobs.expiringSoon || 0) + (dataCounts.applications.expiringSoon || 0) + (dataCounts.candidates.expiringSoon || 0)} records
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Audit Log */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Purge Audit Log</h3>
          <div className="flex items-center gap-2">
            <select
              value={entityTypeFilter}
              onChange={(e) => {
                setEntityTypeFilter(e.target.value);
                setPage(1);
              }}
              className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-700 dark:text-gray-300"
            >
              <option value="">All Types</option>
              <option value="job">Jobs</option>
              <option value="application">Applications</option>
              <option value="candidate">Candidates</option>
            </select>
            <button
              onClick={handleExportCsv}
              className="inline-flex items-center gap-1 rounded-md bg-gray-100 dark:bg-gray-800 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              <Download className="h-3 w-3" />
              Export CSV
            </button>
          </div>
        </div>

        {auditLog.length === 0 ? (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-8 text-center">
            <Shield className="h-8 w-8 text-gray-400 dark:text-gray-600 mx-auto mb-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No purge events recorded yet</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
              Events will appear here after the scheduler runs or a manual purge is triggered
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800">
                    <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Date</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Type</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Count</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Trigger</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Policy (days)</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-400">Batch</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {auditLog.map((entry) => (
                    <tr key={entry.id} className="bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-4 py-2 text-gray-700 dark:text-gray-300 text-xs">
                        {new Date(entry.purgedAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          entry.entityType === 'job'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                            : entry.entityType === 'application'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300'
                        }`}>
                          {entry.entityType}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                        {entry.entityCount}
                      </td>
                      <td className="px-4 py-2 text-gray-500 dark:text-gray-400 text-xs">
                        {entry.triggerType}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-500 dark:text-gray-400">
                        {entry.retentionPolicyDays ?? '-'}
                      </td>
                      <td className="px-4 py-2 text-gray-400 dark:text-gray-500 text-xs font-mono truncate max-w-[100px]" title={entry.batchId ?? ''}>
                        {entry.batchId?.slice(0, 8) ?? '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-3">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Page {pagination.page} of {pagination.totalPages} ({pagination.total} entries)
                </p>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                    disabled={page >= pagination.totalPages}
                    className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
